import { useApiPost } from '@/api/main/query'
import { ApiError, PaginatedCollection } from '@/api/types'
import { SortDirection } from '@/entities/sort'

import { useState } from 'react'
import { withdrawsAPITrader } from '../../api'
import { ActivePayoutDialog, CanceledPayout, CancelPayoutDialog, ManageReceiptsDialog } from '../../dialogs'
import { useWithdrawsModal } from '../../hooks'
import { ConfirmSendData, StartPayingWithdrawModel, PaidWithdrawModel } from '../../model'
import { getColumnsInProgress } from '../../table'
import { MobileBlockTraderWithdraw } from '../mobile/mobileBlocks/MobileBlockTraderWithdraw'
import { BaseTable, BaseTableSkeleton } from 'syncra-storybook/features'

export const AcceptedPanel = ({
  acceptedWithdraws,
  acceptedWithdrawsError,
  isAcceptedWithdrawsLoading,
  isConfirmWithdrawLoading,
  isCancelWithdrawLoading,
  methods,
  skip,
  sort,
  toggleSort,
}: {
  acceptedWithdraws: PaginatedCollection<PaidWithdrawModel[]> | undefined
  acceptedWithdrawsError: ApiError | null
  isAcceptedWithdrawsLoading: boolean
  isConfirmWithdrawLoading: boolean
  isCancelWithdrawLoading: boolean
  methods: {
    handlePageChange: (newSkip: number) => void
    confirmWithdrawPayment: (data: ConfirmSendData) => void
    declineWithdraw: (data: { withdrawId: string; traderReason: string }) => void
  }
  skip?: number
  sort?: {
    sortCreatedAt?: SortDirection
    sortAmountTo?: SortDirection
    sortAllowedAt?: SortDirection
  }
  toggleSort?: (key: 'sortCreatedAt' | 'sortAmountTo' | 'sortAllowedAt') => void
  // status: 'All' | 'inProgress' | 'waiting' | 'paid' | 'failed'
}) => {
  const { modals, actions, setters } = useWithdrawsModal()
  const [activeWithdrawId, setActiveWithdrawId] = useState<string>('')
  const [activeWithdraw, setActiveWithdraw] = useState<StartPayingWithdrawModel | null>(null)

  const { mutate, isPending } = useApiPost<StartPayingWithdrawModel, string>(
    withdrawsAPITrader.getActiveWithdraw(activeWithdrawId),
    {
      onSuccess: (data) => {
        setActiveWithdraw(data)
      },
    }
  )

  const onAction = (withdrawId: string) => {
    setActiveWithdrawId(withdrawId)
    actions.openActiveModal()
    mutate(withdrawId)
  }

  const onCancelModalHandler = () => {
    actions.closeActiveModal()
    actions.openCancelModal()
  }

  const declineWithdrawHandler = ({
    withdrawId,
    traderReason,
  }: {
    withdrawId: string
    traderReason: string
  }) => {
    methods.declineWithdraw({ withdrawId, traderReason })
  }

  const [isReceiptsDialogOpen, setIsReceiptsDialogOpen] = useState(false)
  const [selectedWithdrawId, setSelectedWithdrawId] = useState<string | null>(null)

  const handleManageReceipts = (withdrawId: string) => {
    setSelectedWithdrawId(withdrawId)
    setIsReceiptsDialogOpen(true)
  }

  const handleCloseReceiptsDialog = () => {
    setIsReceiptsDialogOpen(false)
    setSelectedWithdrawId(null)
  }

  return (
    <div className="mt-8">
      {acceptedWithdrawsError ? (
        <p>Ошибка загрузки данных.</p>
      ) : isAcceptedWithdrawsLoading ? (
        <BaseTableSkeleton />
      ) : !acceptedWithdraws?.items || acceptedWithdraws.items?.length === 0 ? (
        <p>Исходящих заявок нет.</p>
      ) : (
        <BaseTable
          columns={getColumnsInProgress({
            onManageReceipts: handleManageReceipts,
            onAction,
            disabled: isPending,
            sort,
            toggleSort,
          })}
          data={acceptedWithdraws.items}
          cardClasses="border-0"
          onPageChange={methods.handlePageChange}
          skip={skip}
          totalItems={acceptedWithdraws.count}
          mobileCards={acceptedWithdraws.items.map((el) => (
            <MobileBlockTraderWithdraw key={el.id} withdraw={el} onAction={() => onAction(el.id)} />
          ))}
        />
      )}

      {/* Модалки */}
      {modals.modalActiveWithdrawVisible && activeWithdraw && (
        <ActivePayoutDialog
          isOpen={modals.modalActiveWithdrawVisible}
          onClose={actions.closeActiveModal}
          onAction={methods.confirmWithdrawPayment}
          onCancel={onCancelModalHandler}
          withdraw={activeWithdraw}
          isLoading={isConfirmWithdrawLoading}
        />
      )}
      {activeWithdraw && (
        <>
          <CancelPayoutDialog
            isOpen={modals.modalCancelLeadVisible}
            onClose={actions.closeCancelModal}
            onAction={declineWithdrawHandler}
            onStepBack={() => {
              actions.closeCancelModal()
              actions.openActiveModal()
            }}
            withdrawId={activeWithdraw.id}
            isLoading={isCancelWithdrawLoading}
          />
          <CanceledPayout
            modalConfirmation={modals.modalConfirmationCancel}
            setModalConfirmation={setters.setModalConfirmationCancel}
            setModalCancel={setters.setModalCancelLeadVisible}
          />
        </>
      )}
      {selectedWithdrawId && (
        <ManageReceiptsDialog
          isOpen={isReceiptsDialogOpen}
          onClose={handleCloseReceiptsDialog}
          withdrawId={selectedWithdrawId}
        />
      )}
    </div>
  )
}
